import React, { useEffect, useState } from "react";

/**
 * RevealImageToggle
 *
 * - Two-option image toggle (e.g., "Original" / "Obfuscated")
 * - Includes optional description text under the buttons
 * - Maintains blue offset frame from your existing ScreenTimeQuiz look
 */
export default function RevealImageToggle({
    options = ["Original", "Obfuscated"],
    imagesByOption = {},
    description = "", // ✨ new optional description field
    initial = null,
    frameVariant = "offset-fill-right", // matches your existing frame
    containerMaxWidth = "max-w-4xl",
    heightPx = 520,
}) {
    const [active, setActive] = useState(initial || options[0]);

    useEffect(() => {
        // preload both images for smooth toggle
        options.forEach((opt) => {
            const src = imagesByOption[opt];
            if (!src) return;
            const img = new Image();
            img.src = src;
        });
    }, [imagesByOption, options]);

    // keep your existing frame styling
    const backClass =
        frameVariant === "offset-fill-right"
            ? "absolute -bottom-3 -right-2 w-full h-full bg-blue-700 rounded-sm"
            : "absolute -bottom-3 -left-3 w-full h-full border-4 border-blue-700 rounded-sm";

    const frontClass =
        "relative bg-white border-2 border-blue-700 rounded-sm overflow-hidden";

    const imgStyle = {
        width: "100%",
        height: `${heightPx}px`,
        objectFit: "cover",
        display: "block",
        transition: "opacity 350ms ease",
    };

    return (
        <div className={`w-full ${containerMaxWidth} mx-auto px-4 py-16`}>
            {/* Frame */}
            <div className="relative w-full flex justify-center mb-6">
                <div className="relative w-full">
                    <div className={backClass} aria-hidden="true" />
                    <div className={`${frontClass} p-0`}>
                        {/* stack both images and cross-fade */}
                        <div style={{ position: "relative", width: "100%", height: `${heightPx}px` }}>
                            {options.map((opt) => {
                                const src = imagesByOption[opt];
                                if (!src) return null;
                                const isActive = active === opt;
                                return (
                                    <img
                                        key={opt}
                                        src={src}
                                        alt={opt}
                                        style={{
                                            ...imgStyle,
                                            position: "absolute",
                                            left: 0,
                                            top: 0,
                                            opacity: isActive ? 1 : 0,
                                            pointerEvents: isActive ? "auto" : "none",
                                        }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-center gap-4">
                {options.map((opt) => {
                    const isActive = active === opt;
                    return (
                        <button
                            key={opt}
                            onClick={() => setActive(opt)}
                            className={`px-6 py-2 rounded-md font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 transition 
              ${isActive
                                    ? "bg-blue-700 text-white shadow-md"
                                    : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200"
                                }`}
                            aria-pressed={isActive}
                        >
                            {opt}
                        </button>
                    );
                })}
            </div>

            {/* ✨ Description field (optional) */}
            {description && (
                <p className="text-center text-gray-600 italic max-w-2xl mx-auto mt-5 leading-relaxed">
                    {description}
                </p>
            )}
        </div>
    );
}
